/**
 * Public Vote page — big saffron "Vote for SociBaba" CTA with a running tally.
 * Every submission is anonymous by design (cookie-based voter_id) but voters
 * can attach an optional name + role for the founder's roll-call.
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SEO } from "@/components/shared/SEO";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Loader2, Heart, Users, Clock, TrendingUp, Mail } from "lucide-react";

interface Tally { total: number; last_hour: number; last_24h: number; as_of: string }
interface RecentItem { vote_id: string; voter_name?: string | null; voter_role?: string | null; comment?: string | null; created_at: string }

const VOTER_ID_KEY = "sb_vote_voter_id";
const VOTED_FLAG_KEY = "sb_vote_cast";

function getVoterId(): string {
  let v = window.localStorage.getItem(VOTER_ID_KEY);
  if (!v) {
    v = `v_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
    window.localStorage.setItem(VOTER_ID_KEY, v);
  }
  return v;
}

const ROLES = ["Judge", "Contest organiser", "Founder / builder", "Attendee", "Investor", "Journalist", "Other"];

export default function VotePage() {
  const qc = useQueryClient();
  const location = useLocation();
  const kiosk = React.useMemo(
    () => new URLSearchParams(location.search).get("kiosk") === "1",
    [location.search],
  );
  const [voted, setVoted] = React.useState<boolean>(() => window.localStorage.getItem(VOTED_FLAG_KEY) === "1");
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("Judge");
  const [comment, setComment] = React.useState("");
  const [voterEmail, setVoterEmail] = React.useState("");
  const [notifyClip, setNotifyClip] = React.useState(true);

  const tally = useQuery<Tally>({
    queryKey: ["vote-tally"],
    queryFn: async () => (await api.get("/vote/tally")).data,
    refetchInterval: 5_000,
  });

  const recent = useQuery<{ items: RecentItem[] }>({
    queryKey: ["vote-recent"],
    queryFn: async () => (await api.get("/vote/recent?limit=8")).data,
    refetchInterval: 8_000,
  });

  const cast = useMutation({
    mutationFn: async () => (await api.post("/vote", {
      voter_id: getVoterId(),
      voter_name: name.trim() || undefined,
      voter_role: role || undefined,
      comment: comment.trim() || undefined,
      voter_email: voterEmail.trim() || undefined,
      notify_clip: !!(voterEmail.trim() && notifyClip),
    })).data,
    onSuccess: (res) => {
      window.localStorage.setItem(VOTED_FLAG_KEY, "1");
      setVoted(true);
      toast.success(res?.duplicate ? "Vote already recorded — note updated." : "Thank you for voting for SociBaba 🧡");
      qc.invalidateQueries({ queryKey: ["vote-tally"] });
      qc.invalidateQueries({ queryKey: ["vote-recent"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Vote failed"),
  });

  return (
    <div className="min-h-screen bg-[color:var(--sg-bg)]" data-testid="vote-page">
      <SEO title="Vote for SociBaba — AI App Builders Contest" path="/vote"/>
      {kiosk ? (
        <VoteKiosk tally={tally.data} recent={recent.data?.items || []}/>
      ) : (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <div className="rounded-3xl overflow-hidden relative border border-[color:var(--sg-border)] mb-8">
          <div className="absolute inset-0 bg-gradient-to-br from-[color:var(--sb-navy,#0A1128)] via-fuchsia-800 to-[color:var(--sb-saffron,#F97316)]"/>
          <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 15% 40%, rgba(249,115,22,0.35), transparent 40%), radial-gradient(circle at 85% 60%, rgba(217,70,239,0.4), transparent 40%)" }}/>
          <div className="relative px-6 sm:px-10 py-10 sm:py-14 text-white">
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/70 flex items-center gap-2">
              <Sparkles className="h-3 w-3" strokeWidth={2.5}/> AI App Builders Contest · July 2026
            </div>
            <h1 className="mt-3 font-serif-display text-4xl sm:text-6xl font-bold leading-tight">
              Vote for <span className="italic">SociBaba™</span>
            </h1>
            <p className="mt-3 text-base sm:text-lg text-white/85 max-w-2xl">
              The AI operator that turns every social signal — every comment, review and mention —
              into revenue action. Automatically.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div data-testid="vote-tally-total" className="rounded-2xl bg-white/95 text-[color:var(--sb-navy,#0A1128)] px-5 py-3 shadow-lg">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--sb-saffron,#F97316)]">Votes so far</div>
                <div className="font-serif-display text-3xl sm:text-4xl font-bold tabular-nums">
                  {tally.data?.total ?? "—"}
                </div>
              </div>
              <div className="rounded-2xl bg-white/15 backdrop-blur-sm text-white px-5 py-3 border border-white/20 flex items-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-white/60">Last hour</div>
                  <div className="font-serif-display text-2xl font-bold tabular-nums">{tally.data?.last_hour ?? "—"}</div>
                </div>
                <div className="h-8 w-px bg-white/25"/>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-white/60">Last 24h</div>
                  <div className="font-serif-display text-2xl font-bold tabular-nums">{tally.data?.last_24h ?? "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6">
          {/* Vote form */}
          <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6 sm:p-8" data-testid="vote-form">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[color:var(--sg-muted)]">
              One tap to vote
            </div>
            <div className="font-serif-display text-2xl sm:text-3xl font-bold text-[color:var(--sg-fg)] mt-1">
              {voted ? "Your vote is in — thank you 🧡" : "Cast your vote for SociBaba"}
            </div>
            <p className="text-sm text-[color:var(--sg-muted)] mt-1">
              {voted
                ? "Feel free to update your note or role below. Duplicate submissions won't inflate the tally."
                : "Anonymous by default. Add your name + role if you're happy to be counted publicly."}
            </p>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                data-testid="vote-input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                maxLength={80}
                className="h-11 bg-white dark:bg-slate-900/40"
              />
              <select
                data-testid="vote-input-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="h-11 rounded-md border border-[color:var(--sg-border)] bg-white dark:bg-slate-900/40 px-3 text-sm text-[color:var(--sg-fg)]"
              >
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Textarea
              data-testid="vote-input-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional — what stood out during the pitch?"
              maxLength={280}
              rows={3}
              className="mt-3 bg-white dark:bg-slate-900/40"
            />

            {/* Email + notify opt-in */}
            <div className="mt-3">
              <Input
                data-testid="vote-input-email"
                value={voterEmail}
                onChange={(e) => setVoterEmail(e.target.value)}
                placeholder="Your email (optional — get the demo clip after the pitch)"
                type="email"
                maxLength={140}
                className="h-11 bg-white dark:bg-slate-900/40"
              />
              {voterEmail.trim() && (
                <label className="mt-2 flex items-center gap-2 text-xs text-[color:var(--sg-fg)] cursor-pointer" data-testid="vote-notify-toggle">
                  <input
                    type="checkbox"
                    checked={notifyClip}
                    onChange={(e) => setNotifyClip(e.target.checked)}
                    className="h-4 w-4 accent-[color:var(--sb-saffron,#F97316)]"
                  />
                  <Mail className="h-3.5 w-3.5 text-[color:var(--sb-saffron,#F97316)]"/>
                  Email me the recorded demo clip after the pitch
                </label>
              )}
            </div>

            <Button
              data-testid="vote-submit"
              onClick={() => cast.mutate()}
              disabled={cast.isPending}
              className="mt-5 w-full h-14 text-base font-bold bg-[color:var(--sb-saffron,#F97316)] hover:bg-[color:var(--sb-saffron-hover,#EA580C)] text-white shadow-lg shadow-orange-500/30"
            >
              {cast.isPending
                ? <><Loader2 className="h-5 w-5 mr-2 animate-spin"/> Casting…</>
                : voted
                  ? <><CheckCircle2 className="h-5 w-5 mr-2"/> Update my vote</>
                  : <><Heart className="h-5 w-5 mr-2 fill-current"/> Vote for SociBaba</>}
            </Button>

            <p className="mt-3 text-[11px] text-[color:var(--sg-muted)] text-center">
              One vote per person. IP-throttled at one every 60s. Your identity is hashed — we only see the name you volunteer.
            </p>
          </div>

          {/* Recent votes wall */}
          <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-6" data-testid="vote-recent">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)] flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3"/> Recent voters
              </div>
              <span className="text-[10px] text-[color:var(--sg-muted)]">Refreshes every 8s</span>
            </div>
            {recent.isLoading ? (
              <div className="text-sm text-[color:var(--sg-muted)]">Loading…</div>
            ) : (recent.data?.items?.length ?? 0) === 0 ? (
              <div className="rounded-xl border border-dashed border-[color:var(--sg-border)] p-8 text-center text-sm text-[color:var(--sg-muted)]">
                <Users className="h-6 w-6 mx-auto mb-2 opacity-60"/>
                Be the first voter. Your name will land here.
              </div>
            ) : (
              <div className="space-y-2">
                {recent.data!.items.map((it) => (
                  <div key={it.vote_id} className="rounded-lg border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-[color:var(--sg-fg)] truncate">
                        {it.voter_name || "Anonymous"}
                        {it.voter_role && <span className="ml-1 text-[10px] font-medium text-[color:var(--sg-muted)] uppercase tracking-wide">· {it.voter_role}</span>}
                      </div>
                      <div className="text-[10px] text-[color:var(--sg-muted)] flex items-center gap-1 shrink-0">
                        <Clock className="h-3 w-3"/>
                        {new Date(it.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    {it.comment && <div className="mt-1 text-xs text-[color:var(--sg-fg)]/80 leading-relaxed">“{it.comment}”</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 text-center text-[11px] text-[color:var(--sg-muted)]">
          <a href="/" className="hover:text-[color:var(--sb-saffron,#F97316)]">← Back to SociBaba</a>
        </div>
      </div>
      )}
    </div>
  );
}

// ─── Kiosk (projector wall) — big tally + rolling wall, no form ─────────────

function VoteKiosk({ tally, recent }: { tally?: Tally; recent: RecentItem[] }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 sm:p-10 bg-gradient-to-br from-[color:var(--sb-navy,#0A1128)] via-fuchsia-900 to-[color:var(--sb-saffron,#F97316)] text-white" data-testid="vote-kiosk">
      <div className="text-[10px] uppercase tracking-[0.5em] font-bold text-white/70 flex items-center gap-2">
        <Sparkles className="h-3 w-3" strokeWidth={2.5}/> AI App Builders Contest · Live tally
      </div>
      <h1 className="mt-4 font-serif-display text-6xl sm:text-8xl font-bold text-center leading-none">
        Vote for <span className="italic">SociBaba™</span>
      </h1>
      <div className="mt-8 flex items-baseline gap-4">
        <div className="font-serif-display text-9xl sm:text-[12rem] font-bold tabular-nums leading-none" data-testid="vote-kiosk-total">
          {tally?.total ?? "—"}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/70">Total votes · live</div>
          <div className="mt-1 flex gap-3 text-sm text-white/80">
            <span>Last hour · <b className="text-white tabular-nums">{tally?.last_hour ?? "—"}</b></span>
            <span>24h · <b className="text-white tabular-nums">{tally?.last_24h ?? "—"}</b></span>
          </div>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 w-full max-w-6xl">
        {recent.slice(0, 8).map((it) => (
          <div key={it.vote_id} className="rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 px-3 py-2">
            <div className="text-sm font-semibold truncate">
              {it.voter_name || "Anonymous"}
              {it.voter_role && <span className="ml-1 text-[10px] font-medium text-white/60 uppercase tracking-wide">· {it.voter_role}</span>}
            </div>
            {it.comment && <div className="mt-1 text-xs text-white/85 leading-relaxed truncate">“{it.comment}”</div>}
          </div>
        ))}
      </div>
      <div className="mt-8 text-[11px] text-white/60 uppercase tracking-widest">
        Tap your phone at socibaba.ai/vote · updates every 5 seconds
      </div>
    </div>
  );
}
